"use client";

import { useState, useEffect, ReactNode } from "react";
import dynamic from "next/dynamic";
import { HotelResponse } from "@/lib/Types/HotelInfoResponse/HotelInfoResponse";
import { Topic } from "@/lib/Types/Topic";
import { ParsedHotelDescription } from "@/lib/Helpers/parseHotelDescription";
import styled from "styled-components";
import media from "@/styles/mediaQueries";
import TabOverview from "./tabs/TabOverview";
import MainFacilities from "./components/MainFacilities";
import { AppConfig } from "@/lib/Types/AppConfig";
import { Text, Dialog } from "@lmn/design-system";
import TextButton from "@/components/UI/TextButton/TextButton";
import { useScreenSize } from "@/lib/Hooks/useScreenSize";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";

const TabAmenities = dynamic(() => import("./tabs/TabAmenities"), {
  ssr: false,
});
const TabHotelInfo = dynamic(() => import("./tabs/TabHotelInfo"), {
  ssr: false,
});
const TabReviews = dynamic(() => import("./tabs/TabReviews"), {
  ssr: false,
});

const Container = styled.div`
  margin: 0;
  border: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
  margin-bottom: 2rem;
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid
    ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  flex: 1;
  padding: 1rem 1.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  position: relative;
  outline: none;
  white-space: nowrap;
  font-family: ${({ theme }) => theme.kallisto.font.family.sans};
  font-weight: ${({ theme }) => theme.kallisto.font.weight.medium};
  font-size: ${({ theme }) => theme.kallisto.font.size[200]};
  line-height: ${({ theme }) => theme.kallisto.font.lineHeight[300]};
  color: ${({ $isActive, theme }) =>
    $isActive
      ? theme.kallisto.color.text.interactive.textPrimaryDefault
      : theme.kallisto.color.text.static.textPrimary};
  transition: color 0.1s ease-in-out, background-color 0.1s ease-in-out,
    box-shadow 0.1s ease-in-out;

  &:hover {
    background-color: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundNeutralHover};
    color: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryHover};
  }

  &:active {
    background-color: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundNeutralPressed};
    color: ${({ theme }) =>
    theme.kallisto.color.text.interactive.textPrimaryPressed};
  }

  &:focus-visible {
    border-radius: ${({ theme }) => theme.kallisto.border.radius.radius100};
    outline: 2px ${({ theme }) => theme.kallisto.color.common.white[1000]} solid;
    outline-offset: 0;
    box-shadow: inset 0 0 0 2px
      ${({ theme }) => theme.kallisto.color.common.black[1000]};
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background-color: ${({ theme }) =>
    theme.kallisto.color.background.interactive.backgroundPrimaryDefault};
    transform: scaleX(${({ $isActive }) => ($isActive ? 1 : 0)});
  }
`;

const TabContent = styled.div`
  padding: 1.5rem 0;
`;

const TabPanel = styled.div<{ $isActive: boolean }>`
  display: ${({ $isActive }) => ($isActive ? "block" : "none")};
`;

// Mobile layout styled components
const MobileContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space150};
  margin-bottom: ${({ theme }) => theme.kallisto.space.space150};
  ${media.screenMdMin`
    display: none;
  `}
`;

const MobileSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space100};
`;

const DescriptionPreview = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;


type TabId = "overview" | "hotelInfo" | "amenities" | "reviews";

const TABS: { id: TabId; label: string }[] = [
  { id: "overview", label: "Überblick" },
  { id: "hotelInfo", label: "Hotel info" },
  { id: "reviews", label: "Bewertungen" },
  { id: "amenities", label: "Ausstattung" },
];

export default function HotelInfoClient({
  hotelInfoData,
  mapBoxApiKey,
  parsedHotelDescription,
  fallbackDescriptionElement,
  appConfig,
}: {
  hotelInfoData: HotelResponse;
  mapBoxApiKey: string;
  parsedHotelDescription: ParsedHotelDescription | null;
  fallbackDescriptionElement: ReactNode;
  appConfig: AppConfig;
}) {
  const [selectedTab, setSelectedTab] = useState<TabId>("overview");
  const [isReviewsDialogOpen, setIsReviewsDialogOpen] = useState(false);
  const [isHotelInfoDialogOpen, setIsHotelInfoDialogOpen] = useState(false);
  const [isAmenitiesDialogOpen, setIsAmenitiesDialogOpen] = useState(false);

  const { isDesktop } = useScreenSize();
  const searchParams = useReactiveSearchParams();
  const searchId = searchParams.get("searchId") ?? "";

  const logInteraction = (message: string) => {
    sendKibanaLog({ envConfig: appConfig.envConfig, message, category: "interaction", searchId });
  };

  // Listen to custom selectHotelInfoTab event to allow scrolling to the right tab
  useEffect(() => {
    const handleSelectTab = (event: CustomEvent<TabId>) => {
      if (!isDesktop) {
        switch (event.detail) {
          case "reviews":
            setIsReviewsDialogOpen(true);
            sendKibanaLog({ envConfig: appConfig.envConfig, message: "All Reviews Dialog Opened", category: "interaction", searchId });
            break;
          case "hotelInfo":
            setIsHotelInfoDialogOpen(true);
            break;
          case "amenities":
            setIsAmenitiesDialogOpen(true);
            break;
        }
      } else {
        setSelectedTab(event.detail);
      }
    };
    window.addEventListener(
      "selectHotelInfoTab",
      handleSelectTab as EventListener
    );
    return () =>
      window.removeEventListener(
        "selectHotelInfoTab",
        handleSelectTab as EventListener
      );
  }, [isDesktop, appConfig.envConfig, searchId]);

  const handleKeyDown = (e: React.KeyboardEvent, currentIndex: number) => {
    let newIndex = currentIndex;

    if (e.key === "ArrowRight") {
      newIndex = (currentIndex + 1) % TABS.length;
    } else if (e.key === "ArrowLeft") {
      newIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    } else if (e.key === "Home") {
      newIndex = 0;
    } else if (e.key === "End") {
      newIndex = TABS.length - 1;
    } else {
      return;
    }

    e.preventDefault();
    setSelectedTab(TABS[newIndex].id);
    document.getElementById(`tab-${TABS[newIndex].id}`)?.focus();
  };

  const topics = hotelInfoData?.hotelData?.topics;

  const hasCategoryListData = topics?.some(
    (topic) => topic.categoryList.length > 0
  ) || false;

  const hasEnoughCategoryListData = topics
    ? topics.reduce((count: number, topic: Topic) => count + topic.categoryList.length, 0) >= 8
    : false;

  // Description preview for mobile layout
  const firstSection = parsedHotelDescription?.sections?.[0]?.content;
  const secondSection = parsedHotelDescription?.sections?.[1]?.content;
  const descriptionPreview = [firstSection, secondSection]
    .filter(Boolean)
    .join("\n\n");

  const headlines = hotelInfoData.hotelData.headlines || [];

  return (
    <div id="hotel-info">
      {/* Desktop: existing tab layout */}
      <Container className="hidden-on-sm">
        <TabsContainer role="tablist" aria-label="Hotel information" data-testid="hotel-info-tabs">
          {TABS.map((tab, index) => (
            <Tab
              key={tab.id}
              role="tab"
              id={`tab-${tab.id}`}
              data-testid={`tab-${tab.id}`}
              aria-selected={selectedTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              tabIndex={selectedTab === tab.id ? 0 : -1}
              $isActive={selectedTab === tab.id}
              onClick={() => {
                setSelectedTab(tab.id);
                logInteraction(`Hotel Info Tab Navigated: ${tab.id}`);
              }}
              onKeyDown={(e) => handleKeyDown(e, index)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>
        <TabContent>
          <TabPanel
            $isActive={selectedTab === "overview"}
            role="tabpanel"
            id="tabpanel-overview"
            data-testid="tabpanel-overview"
            aria-labelledby="tab-overview"
          >
            <TabOverview
              hotelInfoData={hotelInfoData}
              mapBoxApiKey={mapBoxApiKey}
              onSeeAllAmenities={() => setSelectedTab("amenities")}
              onSeeHotelInfo={() => setSelectedTab("hotelInfo")}
              hasCategoryListData={hasCategoryListData && hasEnoughCategoryListData}
              parsedDescription={parsedHotelDescription}
              fallbackDescriptionElement={fallbackDescriptionElement}
              envConfig={appConfig.envConfig}
              searchId={searchId}
            />
          </TabPanel>
          <TabPanel
            $isActive={selectedTab === "hotelInfo"}
            role="tabpanel"
            id="tabpanel-hotelInfo"
            data-testid="tabpanel-hotelInfo"
            aria-labelledby="tab-hotelInfo"
          >
            <TabHotelInfo parsedDescription={parsedHotelDescription} fallbackDescriptionElement={fallbackDescriptionElement} appConfig={appConfig} />
          </TabPanel>
          <TabPanel
            $isActive={selectedTab === "reviews"}
            role="tabpanel"
            id="tabpanel-reviews"
            data-testid="tabpanel-reviews"
            aria-labelledby="tab-reviews"
          >
            <TabReviews cid={hotelInfoData.hotelData?.id?.toString() ?? ""} appConfig={appConfig} searchId={searchId} />
          </TabPanel>
          <TabPanel
            $isActive={selectedTab === "amenities"}
            role="tabpanel"
            id="tabpanel-amenities"
            data-testid="tabpanel-amenities"
            aria-labelledby="tab-amenities"
          >
            <TabAmenities topics={hotelInfoData.hotelData.topics || []} hasCategoryListData={hasCategoryListData} />
          </TabPanel>
        </TabContent>
      </Container>

      {/* Mobile: stacked sections */}
      <MobileContainer>
        {/* Main amenities (horizontal scroll) */}
        {hasCategoryListData && (
          <MobileSection onClick={() => setIsAmenitiesDialogOpen(true)} role="button" tabIndex={0} style={{ cursor: "pointer" }}>
            <MainFacilities headlines={headlines} />
          </MobileSection>
        )}

        {/* Hotel Info preview */}
        {(descriptionPreview || fallbackDescriptionElement) && (
          <MobileSection>
            <Text variant="Title5">Hotel Info</Text>
            <DescriptionPreview variant="BodySmall">
              {descriptionPreview || fallbackDescriptionElement}
            </DescriptionPreview>
            <TextButton onClick={() => setIsHotelInfoDialogOpen(true)} style={{ alignSelf: "flex-start" }}>
              Mehr anzeigen
            </TextButton>
          </MobileSection>
        )}
      </MobileContainer>

      {/* Mobile Dialogs */}
      <Dialog
        isOpen={isReviewsDialogOpen}
        onClose={() => setIsReviewsDialogOpen(false)}
        bodyOverflowY="scroll"
        fullHeight
        bodyPaddingXS={{ hor: 'space50', ver: 'space250' }}
        bodyPaddingS={{ hor: 'space50', ver: 'space250' }}
        header={{
          title: "Bewertungen",
          sticky: true,
        }}
      >
        <TabReviews
          cid={hotelInfoData.hotelData?.id?.toString() ?? ""}
          appConfig={appConfig}
        />
      </Dialog>

      <Dialog
        isOpen={isHotelInfoDialogOpen}
        onClose={() => setIsHotelInfoDialogOpen(false)}
        bodyOverflowY="scroll"
        fullHeight
        bodyPaddingXS={{ hor: 'space50', ver: 'space250' }}
        bodyPaddingS={{ hor: 'space50', ver: 'space250' }}
        header={{
          title: "Hotel Info",
          sticky: true,
        }}
      >
        <TabHotelInfo
          parsedDescription={parsedHotelDescription}
          fallbackDescriptionElement={fallbackDescriptionElement}
          appConfig={appConfig}
        />
      </Dialog>

      <Dialog
        isOpen={isAmenitiesDialogOpen}
        onClose={() => setIsAmenitiesDialogOpen(false)}
        bodyOverflowY="scroll"
        bodyPaddingXS={{ hor: 'space50', ver: 'space250' }}
        bodyPaddingS={{ hor: 'space50', ver: 'space250' }}
        header={{
          title: "Ausstattung",
          sticky: true,
        }}
      >
        <TabAmenities
          topics={hotelInfoData.hotelData.topics || []}
          hasCategoryListData={hasCategoryListData}
        />
      </Dialog>
    </div>
  );
}
